/**
 * src/gm/employees/runner.ts — 阶段执行器（设计稿 §2：session 化的 runWithRetry 骨架）。
 *
 * 与旧版 runWithRetry 的本质差异：对话历史、轮次、续写标记、部分产物全部持久化在
 * SessionRecord 里——重试循环的每一步落盘，崩溃后可从同一对话的同一轮续跑，
 * 已烧的 token 不打水漂。
 *
 * 流程：载入/新建会话 → （崩溃恢复：partialRaw 续写）→ 调用 → 追加 assistant raw 落盘 →
 * 解析校验 → 失败附 issues 进同一对话重试（≤2 轮）→ 成功 closed / 耗尽 exhausted。
 * usage 记账：每次响应的 prompt+completion 精确累计进会话（含全部重试与续写）。
 */

import { emitLive } from '../events/live-bus'
import { streamSnapshot, warn } from '../events/event-log'
import { MAX_VALIDATE_ROUNDS, PARTIAL_SNAPSHOT_MIN_CHARS } from '../platform/config'
import { GmError, ParseError } from '../platform/errors'
import type { ModelChoice } from '../platform/model-map'
import {
  createSession,
  type SessionRecord,
  type StageKind,
} from '../session/session-codec'
import { loadSession, saveSession } from '../session/session-store'
import { STREAM_INTERRUPTED, type ApiClient, type ChatMessage, type Usage } from './api-client'
import { retryFeedback } from './parser'

export interface StageRunOptions<T> {
  projectId: string
  stage: StageKind
  /** 事件流里的"当前干活的人"（员工名 / 评审笔名） */
  stageLabel: string
  employeeId: string
  choice: ModelChoice
  /** 首次会话的 prompt 组装（恢复路径不调用——messages 已在会话里） */
  buildMessages: () => ChatMessage[]
  responseFormat?: Record<string, unknown>
  stream?: boolean
  parse: (raw: string) => T
  /** 程序岗专用：截断/断流时允许续写 + partialRaw 崩溃恢复；其余阶段不传 */
  extractPartial?: (raw: string) => string | null
}

export interface StageOutput<T> {
  payload: T
  usage: Usage
  session: SessionRecord
}

function addUsage(a: Usage, b: Usage): Usage {
  return {
    prompt_tokens: a.prompt_tokens + b.prompt_tokens,
    completion_tokens: a.completion_tokens + b.completion_tokens,
    total_tokens: a.total_tokens + b.total_tokens,
    reasoning_tokens: (a.reasoning_tokens ?? 0) + (b.reasoning_tokens ?? 0),
  }
}

function continuationPrompt(tail: string, interrupted: boolean): string {
  return (
    `输出因${interrupted ? '传输中断' : '长度限制'}被截断。已输出内容的末尾如下：\n…${tail}\n` +
    `请从断点处继续，只输出剩余部分，不要重复已输出的任何内容。`
  )
}

export class EmployeeRunner {
  private readonly api: ApiClient

  constructor(api: ApiClient) {
    this.api = api
  }

  async runStage<T>(opts: StageRunOptions<T>): Promise<StageOutput<T>> {
    let session = loadSession(opts.projectId, opts.stage)
    if (session && session.state === 'closed') {
      // 已完成阶段被重复驱动——内核的幂等守卫漏了，属编排错误，不静默重跑（防双倍烧 token）
      throw new GmError('BAD_STATUS', `${opts.stageLabel} 这个阶段的会话已封盘，不该重跑。`)
    }
    if (!session) {
      session = createSession({
        projectId: opts.projectId,
        stage: opts.stage,
        employeeId: opts.employeeId,
        modelChoice: opts.choice,
        messages: opts.buildMessages(),
      })
      saveSession(session)
    }

    // 流式增量 → live-bus（瞬时打字机）+ 节流 stream-snapshot（持久快照）+
    // partialRaw 节流落盘（程序岗崩溃恢复物证，设计稿 §2.3）
    let lastSnapshotAt = 0
    let partial = session.partialRaw ?? ''
    let partialSavedLen = partial.length
    const onToken = (channel: 'content' | 'reasoning', text: string, received: number) => {
      emitLive({ stage: opts.stageLabel, channel, text, received })
      const now = Date.now()
      if (now - lastSnapshotAt >= 500) {
        lastSnapshotAt = now
        streamSnapshot(opts.projectId, opts.stageLabel, channel, received)
      }
      if (opts.extractPartial && channel === 'content') {
        partial += text
        if (partial.length - partialSavedLen >= PARTIAL_SNAPSHOT_MIN_CHARS) {
          partialSavedLen = partial.length
          session.partialRaw = partial
          saveSession(session)
        }
      }
    }

    // 崩溃恢复（设计稿 §6 ③ + B5 复修，会签 §二·2.2）：active 会话 + partialRaw 非空 →
    // 必须让「拼接断点」与「模型认知断点」一致——模型服从的是会话里最后一条待办指令，
    // 与 partialRaw（真·断点物证）未必同处。按 pending 指令的形态分流：
    let resumePrefix = ''
    let appendContinuationMsgs = false
    if (session.state === 'active' && opts.extractPartial && session.partialRaw) {
      const partialRaw = session.partialRaw
      const msgs = session.messages
      const last = msgs[msgs.length - 1]
      const prev = msgs[msgs.length - 2]
      if (!session.continuationUsed) {
        // 路径 A：断流即崩（续写指令尚未进会话）——现场追加 partial + 续写指令
        resumePrefix = partialRaw
        appendContinuationMsgs = true
        session.continuationUsed = true
        saveSession(session)
        warn(opts.projectId, `${opts.stageLabel} 上次的活干到一半，从断点接着写。`)
      } else if (
        last?.role === 'user' &&
        last.content.includes('请从断点处继续') &&
        prev?.role === 'assistant'
      ) {
        // 路径 B1：轮内续写中崩——续写指令锚在旧半截末尾，模型服从它会从旧锚点重出，
        // 与 partialRaw 前缀重复拼接。历史改写：最后一条 assistant 改写为 partialRaw、
        // 续写指令 tail 刷新为其末尾 2K——模型从真·断点续写，不重出已有部分
        msgs[msgs.length - 2] = { role: 'assistant', content: partialRaw }
        msgs[msgs.length - 1] = {
          role: 'user',
          content: continuationPrompt(partialRaw.slice(-2000), true),
        }
        resumePrefix = partialRaw
        saveSession(session)
        warn(opts.projectId, `${opts.stageLabel} 上次的活干到一半，从断点接着写。`)
      } else if (last?.role === 'user' && last.content.includes('上次交付未通过验收')) {
        // 路径 B2：反馈重出轮中崩——pending 指令是"按反馈重出全文"，模型认知是重出，
        // 拼接前缀必须为空，否则旧 partialRaw 会与重出全文重复拼接
        warn(opts.projectId, `${opts.stageLabel} 上次返工到一半，按反馈重出全文。`)
      } else {
        // 稀有形态（如最后是 assistant）：物证作前缀，消息原样
        resumePrefix = partialRaw
        warn(opts.projectId, `${opts.stageLabel} 上次的活干到一半，从断点接着写。`)
      }
    }

    const callOnce = (messages: ChatMessage[]) =>
      this.api.chat({
        messages,
        choice: opts.choice,
        responseFormat: opts.responseFormat,
        stream: opts.stream,
        onToken,
      })

    for (;;) {
      // 本轮调用的消息：正常 = 会话历史；路径 A 恢复 = 历史 + 部分产物 + 续写指令；
      // 路径 B 恢复 = 会话历史（B1 已在恢复块里把历史改写对齐到真·断点，B2 原样重出）
      const callMessages =
        resumePrefix && appendContinuationMsgs
          ? [
              ...session.messages,
              { role: 'assistant' as const, content: resumePrefix },
              { role: 'user' as const, content: continuationPrompt(resumePrefix.slice(-2000), true) },
            ]
          : session.messages

      const result = await callOnce(callMessages)
      session.usage = addUsage(session.usage, result.usage)
      let raw = resumePrefix + result.content
      resumePrefix = '' // 只对恢复首轮生效一次
      appendContinuationMsgs = false

      // 轮内续写协议：'length' 截断 / 断流哨兵 → 末尾 2K 字符作上下文续写，≤1 次
      const truncated = () =>
        result.finishReason === 'length' || result.finishReason === STREAM_INTERRUPTED
      if (truncated() && opts.extractPartial && !session.continuationUsed) {
        session.continuationUsed = true
        const interrupted = result.finishReason === STREAM_INTERRUPTED
        warn(
          opts.projectId,
          interrupted
            ? `${opts.stageLabel} 写到一半断流了，已收到的部分保留，自动续写中……`
            : `${opts.stageLabel} 写太长被截断了，自动续写中……`,
        )
        session.messages.push({ role: 'assistant', content: raw })
        session.messages.push({ role: 'user', content: continuationPrompt(raw.slice(-2000), interrupted) })
        saveSession(session)
        const contResult = await callOnce(session.messages)
        session.usage = addUsage(session.usage, contResult.usage)
        raw += contResult.content
        result.finishReason = contResult.finishReason
        // 续写后的完整 raw 替换消息里的半截，保持历史干净
        session.messages[session.messages.length - 2] = { role: 'assistant', content: raw }
        session.messages.pop()
        saveSession(session)
      } else {
        session.messages.push({ role: 'assistant', content: raw })
        saveSession(session)
      }

      // partialRaw 终态对齐（节流可能漏掉尾部）——只许变长不许倒退
      //（B5 尸检：恢复路径上若让片段覆盖真·断点物证，下一次刷新连拼接原料都没了）
      if (opts.extractPartial && partial !== raw && raw.length >= partial.length) {
        partial = raw
        session.partialRaw = raw
        saveSession(session)
      }

      try {
        const payload = opts.parse(raw)
        session.state = 'closed'
        saveSession(session)
        return { payload, usage: session.usage, session }
      } catch (err) {
        if (!(err instanceof ParseError)) throw err
        if (session.rounds >= MAX_VALIDATE_ROUNDS) {
          session.state = 'exhausted'
          saveSession(session)
          const exhausted = new ParseError(`重试 ${MAX_VALIDATE_ROUNDS} 轮后仍不合格`, err.issues)
          exhausted.raw = raw
          throw exhausted
        }
        // 校验反馈 loop：附失败清单进同一对话重调（每步落盘，崩溃从本轮续跑）
        session.messages.push({ role: 'user', content: retryFeedback(err.issues) })
        session.rounds += 1
        saveSession(session)
        warn(
          opts.projectId,
          `${opts.stageLabel} 的交付没过验收（第 ${session.rounds} 次返工）：${err.issues[0]} 等 ${err.issues.length} 项`,
        )
      }
    }
  }
}
